// NodeSubtitleField.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Utilities
import { getNodeSubtitle, normalizeNodeSubtitleValue } from 'utilities/NodeSubtitle';

// CSS
import './NodeSubtitleField.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * NodeSubtitleField
 *
 * Single-use subtitle input for a node record.
 *
 * @param {object} node - Node object.
 * @param {string} [label] - Field display label used in the placeholder.
 * @param {Function} onSubtitleSaved - Callback when subtitle is saved.
 * @param {boolean} [readOnly=false] - Whether the field is read-only.
 *
 * @returns {ReactNode} - NodeSubtitleField component.
 */
const NodeSubtitleField = ({ node, label, onSubtitleSaved, readOnly = false }) => {
  // ----- STATE ----- //

  const nodeSubtitle = getNodeSubtitle(node);
  const [subtitle, setSubtitle] = useState(nodeSubtitle);

  // ----- MEMOS ----- //

  // Placeholder / aria label derived from the field title.
  const resolvedLabel = String(label || 'Subtitle').trim() || 'Subtitle';
  const placeholder = readOnly ? '' : `Add ${resolvedLabel.toLowerCase()}...`;
  const ariaLabel = `Add ${resolvedLabel.toLowerCase()}`;

  // ----- EFFECTS ----- //

  useEffect(() => {
    setSubtitle(nodeSubtitle);
  }, [nodeSubtitle]);

  // ----- HANDLERS ----- //

  /**
   * handleSubtitleChange
   *
   * Updates the local draft as the user types.
   *
   * @param {React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>} event - Change event.
   */
  const handleSubtitleChange = useCallback((event) => {
    if (readOnly) return;
    setSubtitle(event?.target?.value ?? '');
  }, [readOnly]);

  /**
   * handleSubtitleBlur
   *
   * Persists the normalized subtitle when focus leaves the field.
   */
  const handleSubtitleBlur = useCallback(() => {
    if (readOnly || typeof onSubtitleSaved !== 'function') return;
    onSubtitleSaved(normalizeNodeSubtitleValue(subtitle));
  }, [onSubtitleSaved, readOnly, subtitle]);

  // ----- RENDER ----- //

  return (
    <div className="node-subtitle-field-container">
      <TextInput
        style={{
          height: '220px',
        }}
        value={subtitle}
        placeholder={placeholder}
        multiline
        expandable
        hybrid
        iconSize={22}
        maxLength={10000}
        aria-label={ariaLabel}
        rows={10}
        readOnly={readOnly}
        onBlur={handleSubtitleBlur}
        onChange={readOnly ? undefined : handleSubtitleChange}
      />
    </div>
  );
};

// Dev Tools
NodeSubtitleField.displayName = 'NodeSubtitleField';

export default NodeSubtitleField;
